/**
 * Recon runs, as a panel on the right-hand rail: every dig this room has run,
 * grouped by what it was aimed at, with what each cost and brought back.
 *
 * It is about how the room got here, not one of its findings, so it sits with
 * the room's other tools on the rail rather than on the board. It fetches its
 * own table, and again when `refreshKey` changes (a dig starting or ending).
 */
import { useEffect, useState } from 'react';
import api from '../../services/api';
import type { BoardroomReconRuns, BoardroomReportKey, BoardroomRunFindings } from '../../types';
import ReconRunsReport from './reports/ReconRunsReport';
import { useShowInternals } from '../../contexts/InternalsContext';
import AdminPill, { ADMIN_BOX } from '../common/AdminPill';

export default function RunsPanel({
  boardroomId,
  refreshKey,
  focusRun,
  onOpen,
  onLens,
  onTable,
  seats,
}: {
  /** The room's seats, above the digs: how the room worked, both halves. */
  seats?: React.ReactNode;
  boardroomId: number;
  refreshKey?: unknown;
  /** The dig "see everything it found" asked to open, with its findings. */
  focusRun?: { runId: number; at: number } | null;
  onOpen: (itemId: number) => void;
  onLens?: (tags: string[]) => void;
  onTable?: (key: BoardroomReportKey) => void;
}) {
  const [data, setData] = useState<BoardroomReconRuns | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    api
      .getBoardroomReport<BoardroomReconRuns>(boardroomId, 'recon_runs' as BoardroomReportKey)
      .then(d => {
        if (!alive) return;
        setData(d);
        setError(null);
      })
      .catch(e => alive && setError(e instanceof Error ? e.message : 'Could not load the runs'));
    return () => {
      alive = false;
    };
  }, [boardroomId, refreshKey]);
  // THE SEATS ARE ADMIN-ONLY: tokens per seat, adding and switching them off,
  // and narrowing to one seat's writes are how the room runs, not what it found.
  const internals = useShowInternals();
  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-3" data-testid="runs-panel">
      {/* The seats show whatever the runs table is doing. */}
      {seats && internals && (
        <div className={`mb-4 p-3 ${ADMIN_BOX}`} data-testid="runs-seats">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
            Seats <AdminPill className="normal-case tracking-normal" />
          </div>
          {seats}
        </div>
      )}
      {error ? (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      ) : !data ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      ) : (
        <ReconRunsReport
          data={data}
          onOpen={onOpen}
          onLens={onLens}
          onTable={onTable}
          loadFindings={(runId: number): Promise<BoardroomRunFindings> => api.getBoardroomRunFindings(boardroomId, runId)}
          focusRun={focusRun ?? undefined}
        />
      )}
    </div>
  );
}
